import Hls from 'hls.js';

type Listener = (...args: any[]) => void;

class TinyEmitter {
    private listeners: Record<string, Listener[]> = {};

    on(event: string, listener: Listener) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(listener);
    }

    off(event: string, listener: Listener) {
        const arr = this.listeners[event];
        if (!arr) return;
        this.listeners[event] = arr.filter((l) => l !== listener);
    }

    emit(event: string, ...args: any[]) {
        const arr = this.listeners[event];
        if (!arr) return;
        for (const listener of arr) {
            listener(...args);
        }
    }

    removeAllListeners(event?: string) {
        if (event) {
            delete this.listeners[event];
        } else {
            this.listeners = {};
        }
    }
}

export class HLSMedia extends TinyEmitter {
    static Events = {
        DATA: 'DATA',
        ERROR: 'ERROR',
    } as const;

    private hls: Hls | null = null;
    private src: string;

    constructor(options: { src: string }) {
        super();
        this.src = options.src;
    }

    init(videoElement: HTMLVideoElement | null) {
        if (!videoElement) return;

        if (Hls.isSupported()) {
            if (!this.hls) {
                const config = {
                    // live / low-latency-ish behaviour
                    lowLatencyMode: true,
                    autoStartLoad: true,
                    startPosition: -1, // -1 = live edge

                    // live edge / catch-up
                    liveSyncDurationCount: 2,
                    liveMaxLatencyDurationCount: 4,
                    maxLiveSyncPlaybackRate: 1.2,

                    // buffering / stability
                    maxBufferLength: 5,
                    backBufferLength: 10,
                    maxBufferHole: 0.2,
                    maxFragLookUpTolerance: 0.2,

                    // quality / performance
                    ignoreDevicePixelRatio: true,
                    capLevelOnFPSDrop: true,
                    capLevelToPlayerSize: true,
                    enableWorker: true,
                } as any; // avoid over-strict HlsConfig typings

                const hls = new Hls(config);

                hls.attachMedia(videoElement);

                hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                    hls.loadSource(this.src);
                });

                hls.on(Hls.Events.FRAG_LOADED, () => {
                    this.emit(HLSMedia.Events.DATA);
                });

                hls.on(Hls.Events.ERROR, (_, data) => {
                    if (import.meta.env.VITE_APP_ENVIRONMENT === 'staging') {
                        console.log('HLS Error ', _, data);
                    }

                    if (
                        [
                            'fragLoadError',
                            'fragParsingError',
                            'fragLoadTimeOut',
                            'bufferStalledError',
                            'bufferNudgeOnStall',
                            'levelLoadError',
                            'levelLoadTimeOut',
                        ].includes(data.details)
                    ) {
                        if (data.frag) {
                            // jump ahead to avoid stall loops
                            videoElement.currentTime = data.frag.start + data.frag.duration + 1.0;
                        }
                    }

                    if (data.fatal) {
                        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                            this.hls?.recoverMediaError();
                        } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR && data.details === 'manifestLoadError') {
                            this.emit(HLSMedia.Events.ERROR);
                        }
                    }
                });

                hls.on(Hls.Events.DESTROYING, () => {
                    this.hls = null;
                });

                this.hls = hls;
            }
        } else if (videoElement.canPlayType('application/vnd.apple.mpegURL')) {
            // Native HLS (Safari, etc.)
            if (!videoElement.src) {
                videoElement.src = this.src;

                const onLoadedData = () => {
                    this.emit(HLSMedia.Events.DATA);
                };
                const onError = () => {
                    this.emit(HLSMedia.Events.ERROR);
                };

                videoElement.addEventListener('loadeddata', onLoadedData);
                videoElement.addEventListener('error', onError);
            }
        }
    }

    destroy() {
        if (this.hls) {
            this.hls.stopLoad();
            this.hls.detachMedia();
            this.hls.destroy();
            this.hls = null;
        }
        this.removeAllListeners();
    }
}
